import { BaseButton, BaseButtonText, BaseDialog, useAuth } from '@cks-internal/react-base';
import { useHotelBookingStore } from '../../features/stores/useHotelBookingStore';
import { SiteModeObject } from '../../features/misc/SiteModeObject';
import { Button, Menu, Portal, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useCheckPersonalCardAvailableQuery } from '../../features/userProfileRefactor/api/checkPersonalCardAvailableQuery';
import { siteModeBusiness, siteModeDemo, siteModePersonal } from '../../constants/strings';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBlockQuestion,
  faBriefcase,
  faChevronDown,
  faGamepad,
  faUmbrellaBeach
} from '@fortawesome/pro-regular-svg-icons';
import { useProfileStore } from 'src/features/stores/useProfileStore';
import { useCheckPersonalModeDisabledQuery } from 'src/features/userProfileRefactor/api/checkPersonalModeDisabledQuery';
import { useCheckDemoModeForcedQuery } from 'src/features/userProfileRefactor/api/checkDemoModeForcedQuery';
import { useCheckCompanyCardProfileInfoQuery } from 'src/features/companyProfileManagement/api/checkCompanyCardProfileInfoQuery';
import { useLockSearchFormStore } from 'src/features/stores/useLockSearchFormStore';
import { CompanyPaymentType } from 'src/features/userProfileRefactor/types/OnlineBookingProfileModel';
import { useObtProfileNav } from 'src/globalModels/api/useObtProfileNav';

export const SiteModeSelector = () => {
  const profileStore = useProfileStore();
  const selectedUser = profileStore.users?.find((u) => u.ContactId === profileStore.selectedUserId);
  const auth = useAuth();
  const setHotelSearchState = useHotelBookingStore((state) => state.setSearchQuery);
  const smo = JSON.parse(localStorage.getItem('smo')!) as SiteModeObject;
  const switchModeDisclosure = useDisclosure();
  const bookingModeHelpDisclosure = useDisclosure();
  const warnPersonalCardDisclosure = useDisclosure();
  const warnNoBusinessModeCardDisclosure = useDisclosure();
  const checkPersonalModeDisabledQuery = useCheckPersonalModeDisabledQuery();
  const checkDemoModeForcedQuery = useCheckDemoModeForcedQuery();
  const profileNav = useObtProfileNav();
  const setIsLocked = useLockSearchFormStore((state) => state.setIsLocked);
  const lockedMessage = useLockSearchFormStore((state) => state.message);

  // BusinessMode -> check company setup:
  // (Payment Type + whether company corp card / corp p card exists for current user).
  const checkCompanyCardProfileInfoQuery = useCheckCompanyCardProfileInfoQuery(
    smo && smo.currentMode == siteModeBusiness,
    auth.isAuthenticated
  );

  // PresonalMode -> check card
  const checkPersonalCardAvailableQuery = useCheckPersonalCardAvailableQuery(
    smo && smo.currentMode == siteModePersonal,
    auth.isAuthenticated
  );

  const [disablePersonalMode, setDisablePersonalMode] = useState<boolean>(false);
  const [forceDemoMode, setForceDemoMode] = useState<boolean>(false);
  const [pendingMode, setPendingMode] = useState<string | undefined>();

  useEffect(() => {
    const value = checkPersonalModeDisabledQuery.data;

    if (value == null) {
      setDisablePersonalMode(false);
      return;
    }
    const disable = value === true;
    setDisablePersonalMode(disable);

    if (!forceDemoMode && disable && smo && smo.currentMode == siteModePersonal) {
      finishSwitchMode(siteModeBusiness);
    }
  }, [checkPersonalModeDisabledQuery.data, forceDemoMode]);

  useEffect(() => {
    const value = checkDemoModeForcedQuery.data;

    if (value == null) {
      setForceDemoMode(false);
      return;
    }

    const force = value === true;
    setForceDemoMode(force);

    if (force && smo && smo.currentMode != siteModeDemo) {
      finishSwitchMode(siteModeDemo);
    }
  }, [checkDemoModeForcedQuery.data]);

  useEffect(() => {
    const mode = smo?.currentMode;
    const isValidMode = mode === siteModeBusiness || mode === siteModePersonal || mode === siteModeDemo;

    if (forceDemoMode && mode !== siteModeDemo) {
      finishSwitchMode(siteModeDemo);
      return;
    }

    // Set the default mode to business if we haven't got one.
    if (!isValidMode) {
      finishSwitchMode(siteModeBusiness);
      return;
    }

    if (mode === siteModePersonal) {
      // If we're in personal mode, check for personal card at server.
      if (checkPersonalCardAvailableQuery.data === false) {
        warnPersonalCardDisclosure.onOpen();
      }
      return;
    }

    if (mode === siteModeDemo) {
      setIsLocked(false);
      return;
    }

    // Business mode checks
    const companyInfo = checkCompanyCardProfileInfoQuery.data;

    const lockAndWarn = (message: string) => {
      warnNoBusinessModeCardDisclosure.onOpen();
      setIsLocked(true, message);
    };

    // PaymentType: Corp Card, invalid/missing corp card + is NOT SuperUser.
    if (
      companyInfo &&
      companyInfo.CompanyPaymentType === CompanyPaymentType.CorporateCard &&
      !companyInfo.HasValidCorpCard &&
      !selectedUser?.ObtSuperUser
    ) {
      lockAndWarn(
        `In order to continue with ${siteModeBusiness} bookings, you need to have a Primary Company Card on file.  Please contact your administrator or Frontier.`
      );
      return;
    }

    // PaymentType: Corp Card, invalid/missing corp card + is SuperUser.
    if (
      companyInfo &&
      companyInfo.CompanyPaymentType === CompanyPaymentType.CorporateCard &&
      !companyInfo.HasValidCorpCard &&
      selectedUser?.ObtSuperUser
    ) {
      lockAndWarn(
        `In order to continue with ${siteModeBusiness} bookings, you need to have a Primary Company Card on file.  Please add one in the 'Company Profile' page.`
      );
      return;
    }

    // PaymentType: Corp P Card, invalid/missing corp card.
    if (
      companyInfo &&
      companyInfo.CompanyPaymentType === CompanyPaymentType.CorporatePCard &&
      !companyInfo.HasValidCorpPCard
    ) {
      lockAndWarn(
        `In order to continue with ${siteModeBusiness} bookings, you need to have a Personal Corporate Card on file.  Please add one in the 'My Profile' page.`
      );
      return;
    }

    setIsLocked(false);
  }, [
    checkPersonalCardAvailableQuery.isFetched,
    checkPersonalCardAvailableQuery.data,
    checkCompanyCardProfileInfoQuery.isFetched,
    checkCompanyCardProfileInfoQuery.data,
    selectedUser,
    forceDemoMode
  ]);

  const setMode = (newMode: string) => {
    if (!smo || (forceDemoMode && newMode !== siteModeDemo)) {
      return;
    }

    if (smo.currentMode == newMode) {
      return;
    }

    // Show confirm dialog.
    if (smo && smo.currentMode != newMode) {
      setPendingMode(newMode);
      switchModeDisclosure.onOpen();
      return;
    }

    finishSwitchMode(newMode);
  };

  const onSwitchModeClick = () => {
    switchModeDisclosure.onClose();
    const newMode = pendingMode ?? (smo.currentMode == siteModeBusiness ? siteModePersonal : siteModeBusiness);
    setPendingMode(undefined);
    finishSwitchMode(newMode);
  };

  const finishSwitchMode = (newMode: string) => {
    const siteModeObj: SiteModeObject = {
      currentMode: newMode
    };
    localStorage.setItem('smo', JSON.stringify(siteModeObj));
    setHotelSearchState(undefined);
    window.location.href = '/';
  };

  useEffect(() => {
    if (
      !forceDemoMode &&
      auth.isAuthenticated &&
      selectedUser?.ObtPersonalOnly &&
      smo &&
      smo.currentMode == siteModeBusiness
    ) {
      finishSwitchMode(siteModePersonal);
    }
  }, [auth.isAuthenticated, selectedUser, forceDemoMode]);

  // Disable render if anything missing below.
  if (!selectedUser || !auth.isAuthenticated || !smo || (smo && !smo.currentMode)) {
    return <></>;
  }

  const currentModeIcon =
    smo.currentMode == siteModeBusiness ? faBriefcase : smo.currentMode == siteModeDemo ? faGamepad : faUmbrellaBeach;
  const currentModeColor = smo.currentMode == siteModePersonal ? 'secondary.500' : 'primary.500';

  return (
    <>
      <Menu.Root>
        <Menu.Trigger asChild>
          <Button
            rounded={'full'}
            variant={'outline'}
            cursor={'pointer'}
            minW={0}
            backgroundColor={'white'}
            borderWidth={'2px'}
            borderColor={currentModeColor}
            padding={'0px 8px 0px 8px'}>
            <FontAwesomeIcon icon={currentModeIcon} />
            <Text fontWeight={'semibold'}>{smo.currentMode}</Text>
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content>
              <Menu.ItemGroup>
                <Menu.ItemGroupLabel>Site Mode</Menu.ItemGroupLabel>
                <Menu.Item
                  key={0}
                  value='0'
                  onClick={() => setMode(siteModeBusiness)}
                  disabled={forceDemoMode}
                  fontWeight={smo.currentMode == siteModeBusiness ? 'bold' : 'normal'}>
                  <FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '8px' }} />
                  {siteModeBusiness}
                </Menu.Item>
                {!disablePersonalMode && (
                  <Menu.Item
                    key={1}
                    value='1'
                    onClick={() => setMode(siteModePersonal)}
                    disabled={forceDemoMode}
                    fontWeight={smo.currentMode == siteModePersonal ? 'bold' : 'normal'}>
                    <FontAwesomeIcon icon={faUmbrellaBeach} style={{ marginRight: '8px' }} />
                    {siteModePersonal}
                  </Menu.Item>
                )}
                <Menu.Item
                  key={2}
                  value='2'
                  onClick={() => setMode(siteModeDemo)}
                  fontWeight={smo.currentMode == siteModeDemo ? 'bold' : 'normal'}>
                  <FontAwesomeIcon icon={faGamepad} style={{ marginRight: '8px' }} />
                  {siteModeDemo}
                </Menu.Item>
              </Menu.ItemGroup>
              <Menu.ItemGroup>
                <Menu.ItemGroupLabel>Help</Menu.ItemGroupLabel>
                <Menu.Item key={3} value='3' onClick={() => bookingModeHelpDisclosure.onOpen()}>
                  <FontAwesomeIcon icon={faBlockQuestion} style={{ marginRight: '8px' }} />
                  What is this?
                </Menu.Item>
              </Menu.ItemGroup>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>

      {/* Overlay confirm change site mode */}
      <BaseDialog
        placement={'center'}
        disableCloseOnClick={false}
        closeOnEsc={true}
        disclosure={switchModeDisclosure}
        isLoading={false}
        header={'Switch Site Mode?'}
        footerRender={
          <>
            <BaseButton onClick={onSwitchModeClick}>Switch</BaseButton>
          </>
        }
        size={'sm'}>
        <Text paddingTop={'16px'} paddingBottom={'16px'}>
          Switching site mode will restart the booking process from the beginning and lose any current search
          information. Are you sure?
        </Text>
      </BaseDialog>

      {/* Site Mode Help info modal */}
      <BaseDialog
        placement={'center'}
        disableCloseOnClick={false}
        closeOnEsc={true}
        disclosure={bookingModeHelpDisclosure}
        isLoading={false}
        header={'Site Mode Help'}
        footerRender={
          <>
            <BaseButton onClick={bookingModeHelpDisclosure.onClose} buttonText={BaseButtonText.OK} />
          </>
        }
        size={'sm'}>
        <Text paddingTop={'16px'} paddingBottom={'16px'}>
          This selector changes the booking type from company bookings to personal bookings. These bookings are strictly
          for the personal use of the employees of companies that are contracted with Frontier Lodging Solutions.
          <br />
          <br />
          These Frontier hotel rates cannot be used for booking teams or any other group situations. If you use these
          rates for this purpose, your hotel booking may be cancelled by the hotel at their discretion. Personal credit
          card must be used and added in the personal profile section of the site before bookings can be made.
        </Text>
      </BaseDialog>

      {/* Overlay warn no Personal card on file */}
      <BaseDialog
        placement={'center'}
        hideCloseButton={true}
        disableCloseOnClick={true}
        closeOnEsc={false}
        disclosure={warnPersonalCardDisclosure}
        isLoading={false}
        header={'Card Needed On File'}
        footerRender={
          <>
            <BaseButton
              onClick={() => {
                warnPersonalCardDisclosure.onClose();
                profileNav.goToCardSection();
              }}>
              Go to My Profile
            </BaseButton>
          </>
        }
        size={'sm'}>
        <Text paddingTop={'16px'} paddingBottom={'16px'}>
          In order to continue with <strong>{siteModePersonal}</strong> bookings, you need to have a personal card on
          file.
        </Text>
      </BaseDialog>

      {/* Overlay warn no Company Corp card on file */}
      <BaseDialog
        placement={'center'}
        hideCloseButton={true}
        disableCloseOnClick={true}
        closeOnEsc={false}
        disclosure={warnNoBusinessModeCardDisclosure}
        isLoading={false}
        header={'Bookings Disabled'}
        footerRender={
          <>
            <BaseButton
              onClick={() => {
                warnNoBusinessModeCardDisclosure.onClose();
              }}>
              Ok
            </BaseButton>
          </>
        }
        size={'sm'}>
        <VStack gap={'16px'} align={'start'}>
          <Text>{lockedMessage}</Text>
        </VStack>
      </BaseDialog>
    </>
  );
};
